import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { CheckCircle2, XCircle, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSounds } from '@/hooks/useSounds';

export default function FillInBlank({ 
  sentence, // Cümle, boşluk için ___ kullanılır
  correctAnswer,
  options, // Seçenekler varsa gösterilir
  hint,
  onAnswer,
  disabled = false 
}) {
  const [userAnswer, setUserAnswer] = useState('');
  const [isAnswered, setIsAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const { playCorrect, playIncorrect, playClick } = useSounds();

  // Cümleyi parçalara ayır
  const parts = sentence.split('___');

  const handleOptionSelect = (option) => {
    if (isAnswered || disabled) return;
    playClick();
    setUserAnswer(option);
  };

  const handleSubmit = () => {
    if (!userAnswer.trim() || isAnswered || !correctAnswer) return;
    
    const correct = userAnswer.toLowerCase().trim() === correctAnswer.toLowerCase().trim();
    setIsCorrect(correct);
    setIsAnswered(true);
    
    // Ses efekti çal
    if (correct) {
      playCorrect();
    } else {
      playIncorrect();
    }
    
    setTimeout(() => {
      onAnswer?.(correct, userAnswer);
    }, 1500);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Instruction */}
      <div className="mb-4">
        <p className="text-gray-500 text-sm">Boşluğu doldurun</p>
      </div>

      {/* Sentence with blank */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6">
        <p className="text-xl text-gray-800 leading-relaxed">
          {parts[0]}
          <span className={cn(
            'inline-block min-w-[100px] mx-2 px-4 py-1 rounded-lg border-2 border-dashed text-center font-bold',
            isAnswered && isCorrect ? 'border-green-500 bg-green-50 text-green-700' :
            isAnswered && !isCorrect ? 'border-red-500 bg-red-50 text-red-700' :
            userAnswer ? 'border-blue-500 bg-blue-50 text-blue-700' :
            'border-gray-300 bg-gray-50 text-gray-400'
          )}>
            {userAnswer || '?'}
          </span>
          {parts[1]}
        </p>
      </div>

      {/* Options or Input */}
      {options && options.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 mb-4">
          {options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleOptionSelect(option)}
              disabled={isAnswered || disabled}
              className={cn(
                'p-4 rounded-xl border-2 font-medium transition-all duration-200',
                userAnswer === option && !isAnswered ? 'border-blue-500 bg-blue-50 text-blue-700' :
                isAnswered && option.toLowerCase() === correctAnswer.toLowerCase() ? 'border-green-500 bg-green-50 text-green-700' :
                isAnswered && userAnswer === option && !isCorrect ? 'border-red-500 bg-red-50 text-red-700' :
                'border-gray-200 hover:border-blue-300 text-gray-700'
              )}
            >
              {option}
            </button>
          ))}
        </div>
      ) : (
        <div className="mb-4">
          <input
            type="text"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isAnswered || disabled}
            placeholder="Cevabınızı yazın..."
            className={cn(
              'w-full p-4 rounded-xl border-2 text-lg font-medium transition-all',
              isAnswered && isCorrect ? 'border-green-500 bg-green-50' :
              isAnswered && !isCorrect ? 'border-red-500 bg-red-50' :
              'border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
            )}
          />
        </div>
      )}

      {/* Hint */}
      {hint && !isAnswered && (
        <button
          onClick={() => setShowHint(!showHint)}
          className="flex items-center gap-2 text-amber-600 hover:text-amber-700 mb-4"
        >
          <HelpCircle className="w-5 h-5" />
          <span className="text-sm font-medium">İpucu göster</span>
        </button>
      )}
      
      {showHint && !isAnswered && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
          <p className="text-amber-700 text-sm">💡 {hint}</p>
        </div>
      )}

      {/* Submit Button */}
      {!isAnswered && (
        <Button
          onClick={handleSubmit}
          disabled={!userAnswer.trim() || disabled}
          className="w-full py-6 text-lg font-bold rounded-xl bg-blue-500 hover:bg-blue-600"
        >
          Kontrol Et
        </Button>
      )}

      {/* Feedback */}
      {isAnswered && (
        <div className={cn(
          'p-4 rounded-xl',
          isCorrect ? 'bg-green-100 border border-green-200' : 'bg-red-100 border border-red-200'
        )}>
          <div className="flex items-center gap-2 mb-2">
            {isCorrect ? (
              <>
                <CheckCircle2 className="w-6 h-6 text-green-600" />
                <span className="font-bold text-green-700">Mükemmel!</span>
              </>
            ) : (
              <>
                <XCircle className="w-6 h-6 text-red-600" />
                <span className="font-bold text-red-700">Doğru cevap: {correctAnswer}</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
